import { autorun } from "mobx";
import { LearningLogDocument, PersonalDocument, ProblemDocument } from "../../models/document/document-types";
import { BaseListener } from "./base-listener";
class DocumentMonitor {
    constructor(document, path, db) {
        Object.defineProperty(this, "document", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "ref", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "path", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The arrow function is required here so the function will be
        // bound to `this` when used by `ref.on(...)`
        Object.defineProperty(this, "snapshotHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (snapshot) => {
                var _a;
                if (snapshot === null || snapshot === void 0 ? void 0 : snapshot.val()) {
                    const updatedDoc = snapshot.val();
                    const updatedContent = this.db.parseDocumentContent(updatedDoc);
                    (_a = this.document) === null || _a === void 0 ? void 0 : _a.setContent(updatedContent || {});
                }
            }
        });
        this.document = document;
        this.db = db;
        this.path = path;
        this.ref = this.db.firebase.ref(path);
        this.ref.on("value", this.snapshotHandler);
    }
    get description() {
        return `document: ${this.document.key} type: ${this.document.type} path: ${this.path}`;
    }
    off() {
        this.ref.off("value", this.snapshotHandler);
    }
}
export class DBDocumentsContentListener extends BaseListener {
    constructor(db) {
        super("DBDocumentContentListener");
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // key is the document's path in firebase
        Object.defineProperty(this, "documentMonitors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "disposer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getDocumentPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (document) => {
                const { user } = this.db.stores;
                const documentKey = document.key;
                return this.db.firebase.getUserDocumentPath(user, documentKey, document.uid);
            }
        });
        Object.defineProperty(this, "monitorDocument", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (document) => {
                const documentPath = this.getDocumentPath(document);
                if (this.documentMonitors[documentPath]) {
                    // We are already monitoring this document
                    return;
                }
                const documentMonitor = new DocumentMonitor(document, documentPath, this.db);
                this.debugLog("#monitorDocument", documentMonitor.description);
                this.documentMonitors[documentPath] = documentMonitor;
            }
        });
        Object.defineProperty(this, "unmonitorDocumentByPath", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (documentPath) => {
                const documentMonitor = this.documentMonitors[documentPath];
                if (documentMonitor) {
                    this.debugLog("#unmonitorDocument", documentMonitor.description);
                    documentMonitor.off();
                    delete this.documentMonitors[documentPath];
                }
            }
        });
        this.db = db;
    }
    start() {
        this.disposer = autorun(() => {
            const { documents, groups, persistentUI, user } = this.db.stores;
            const documentsToMonitor = [];
            documents.byType(ProblemDocument).forEach((document) => {
                // Besides collecting the documents to monitor, we also update the group
                // its owning user is in. A new document could be added with an out of date
                // group id. Or a user's group can change. In both cases the document
                // should be updated. This tracks that user's membership, not ownership:
                // a problem document stays theirs whatever group they move to.
                document.setGroupIdOfUserOwner(groups.groupIdForUser(document.uid));
                // Users don't monitor their own documents
                if ((document.uid === user.id)) {
                    return;
                }
                // RESEARCHER-ACCESS: should this be changed to isTeacherOrResearcher?
                // teachers monitor all problem documents
                // students only monitor documents in their group to save bandwidth
                if (user.isTeacher || document.groupIdOfUserOwner === user.currentGroupId) {
                    documentsToMonitor.push(document);
                }
            });
            // Monitor personal documents and learning logs, except the document currently
            // open for editing in the workspace (that one is already reactive via its shared
            // in-memory instance, and a remote "value" listener would clobber local edits).
            const { primaryDocumentKey, comparisonDocumentKey } = persistentUI.problemWorkspace;
            const editableKeys = new Set([primaryDocumentKey, comparisonDocumentKey]);
            [PersonalDocument, LearningLogDocument].forEach((type) => {
                documents.byType(type).forEach((document) => {
                    if (editableKeys.has(document.key))
                        return;
                    documentsToMonitor.push(document);
                });
            });
            // Stop monitoring any documents we shouldn't be
            // This could happen if a user changes groups
            Object.keys(this.documentMonitors).forEach(monitoredDocPath => {
                // Should this document be monitored?
                if (!documentsToMonitor.find((document) => this.getDocumentPath(document) === monitoredDocPath)) {
                    this.unmonitorDocumentByPath(monitoredDocPath);
                }
            });
            // Ensure we are monitoring any new documents
            documentsToMonitor.forEach(document => {
                // If the document is already monitored it will not be monitored twice
                this.monitorDocument(document);
            });
        });
    }
    stop() {
        var _a;
        (_a = this.disposer) === null || _a === void 0 ? void 0 : _a.call(this);
        this.unmonitorAllDocuments();
    }
    unmonitorAllDocuments() {
        Object.keys(this.documentMonitors).forEach(documentPath => this.unmonitorDocumentByPath(documentPath));
    }
}
//# sourceMappingURL=data:application/json;base64,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